// HTML5 document envelope for rendered emails. Per spec/tech-arch.md
// §3.2 + spec/build-plan.md P-019 row + spec/design-system.md
// §Email HTML conventions.
//
// **Light-only** (per D-100, P-037 close): the email-output is a
// single light palette — no dark-mode CSS in the shipped HTML. Email
// clients with native dark-mode behaviors (Apple Mail auto-invert;
// Gmail's auto-color-transform) apply their own rendering on top.
// Outlook desktop ignores dark-mode CSS regardless. The half-implemented
// dark-mode `@media (prefers-color-scheme: dark)` rule + the
// `[data-color-scheme="dark"]` rule + the `colorScheme` arg were all
// removed; the in-app preview's Dark toggle is also dropped.
//
// **Embedded `<style>` carries the mobile breakpoint only.** Universal
// `td[width]{width:100%!important;display:block!important}` collapse —
// heavy-handed but simple for V1. Blocks self-wrap to `tokens.layout.
// containerWidth = 680 px` outer tables, so the only multi-column grids
// using fixed-percent `<td>` widths (chapter-whatsapp 3-col, member-list
// 3-col, chapter-sponsors 2-col grid, day-chairs 1/2/3-col, event-card-
// group N-col) stack to single-column on mobile.
// Per-Block class hooks deferred if a Block needs to preserve a width
// on mobile. See D-082.
//
// **`<title>`** is HTML-escaped automatically by the html kernel (D-021 +
// html.ts:`interpolate`) since `title` is interpolated as a plain string,
// not an HtmlString. The Email's `name` field is staff-supplied; HTML5
// only allows raw text in `<title>`, so escaping the five reserved chars
// (`<>&"'`) at the kernel is sufficient.
//
// **Centering wrapper (P-037.26 D-134).** Every Block emits its own
// outer `<table>` constrained to `tokens.layout.containerWidth` (680 px
// post-D-101). Without an enclosing centering wrapper the body content
// hugs the viewport's left edge in any client that respects body
// styles — Banks's P-037.26 View-in-Tab walkthrough surfaced this.
// Wrap the body in an outer `<table align="center" width="${WIDTH}"
// style="margin: 0 auto;">` so:
//
// 1. **Outlook desktop** respects `align="center"` on a fixed-width
//    table (Word rendering engine treats it as the alignment of the
//    table relative to its parent block).
// 2. **Modern browsers** (View-in-Tab Blob URL + every webmail render)
//    respect `margin: 0 auto` on a fixed-width block-level table.
// 3. **iOS Mail / Apple Mail** respect both.
//
// The two attributes are belt-and-suspenders; each picks up the
// clients the other misses. The wrapper carries `width="${WIDTH}"`
// (HTML attribute, Outlook-safe) + `max-width: ${WIDTH}px` (CSS,
// modern browsers) so the 680 px constraint reaches both rendering
// engines. The previously-uncovered case was the View-in-Tab Blob URL
// at full viewport width: without the wrapper, the body content
// extended edge-to-edge of the viewport instead of centering.
//
// **White-card bg over light-gray letterbox (P-037.31 fifth post-Phase-
// Close §A; reinforced at P-048.6 §C / D-149; Outlook fixed via MSO
// conditional at P-048.7 / D-150).** The wrapper table carries
// `bgcolor="#ffffff"` HTML attribute + `background-color: #ffffff;`
// inline style so the 680 px content column reads as a white "card"
// over the body's `bg-page` (`#f1f2f5` post-fourth batch §C). The
// two-layer bg mirrors the width's belt-and-suspenders: `bgcolor` is
// Outlook-desktop-safe; inline `style` covers modern clients. **P-048.6
// §C** — the SAME `bgcolor="#ffffff"` + `background-color: #ffffff;`
// is ALSO applied to the inner `<td>` of the wrapper because Banks's
// post-P-048.5 Outlook desktop send showed gray gaps (body `#f1f2f5`)
// leaking through inter-Block spacers despite the table-level bg.
// Outlook's Word renderer doesn't reliably cascade `<table bgcolor>`
// to transparent child tables — `<td bgcolor>` is the more reliable
// layer that direct-paints the cell behind whatever sits in it. The
// inter-Block spacer cells in `render-email.ts` ALSO carry bgcolor=white
// (the third layer) so even if a renderer ignores both wrapper layers,
// each spacer is explicitly painted white. **P-048.7 / D-150** —
// Banks's continued Outlook smoke after P-048.6 deploy confirmed the
// spacer-cell bgcolor approach STILL didn't paint in Outlook Word
// renderer (the empty-`&nbsp;` + line-height:0 cell is effectively
// contentless from the renderer's perspective; it skips the bg paint
// despite the explicit `bgcolor` attr). Silver bullet: MSO conditional
// `<style>` block in `<head>` overrides body bg to white for Outlook
// ONLY. Modern clients (Apple Mail / iPhone Mail / Gmail web+mobile)
// ignore the conditional and keep the #f1f2f5 letterbox design intent
// from D-138 fifth post-Phase-Close §A. Outlook sees white body,
// transparent spacer cells leak white, no visible gray. The 3-layer
// bgcolor scaffolding (wrapper table + wrapper inner td + spacer cells)
// is retained as belt-and-suspenders — zero harm in any client + covers
// any future renderer that might honor those layers. Hard-coded
// `#ffffff` (not a token) because it's a single use case below the
// 3-use promotion threshold; `tokens.color['text-on-dark']` is
// semantically for text color on a dark surface, not card-bg.
//
// **Grid card cell mobile padding (P-048.6 §A / D-149).** The
// `@media (max-width: 480px)` block adds `.s7gc { padding-top: 20px
// !important; }` (s7gc = "Southern 7 grid cell"). The class is
// emitted by `threeColumnGrid` in `src/components/helpers.ts` on
// every card cell. On desktop the class is unused (no top-level
// rule targets it) — row-1 cells stay flush-top. On mobile the
// `td[width]` collapse rule turns each cell into a 100%-wide block,
// AND the `.s7gc` rule adds `padding-top: 20px` so stacked cells
// have consistent vertical separation. Without the rule, row-1
// cells (no inline padding-top) butt up against each other on
// mobile — Banks's Litmus preview surfaced this for Southern 7 +
// Alabama in chapter-whatsapp. The class-based mobile-only fix
// preserves desktop's flush-top design for event-card-group +
// chapter-sponsors + chapter-whatsapp consumers of `threeColumnGrid`.
//
// **`prettier-ignore`** on the template body — Prettier reformats
// multi-line HTML in a way that breaks the byte-exact golden-file test
// fixture in render-email.test.ts (P-019 carry-forward of the P-016
// lesson).
//
// **CC tracking-pixel placeholder (P-048.1 D-144 Item 2).** Constant
// Contact's open-rate metric requires a `[[trackingImage]]` placeholder
// somewhere inside the email's `<body>`; CC substitutes the literal pixel
// `<img>` at send time. Deferred at D-143 sub-decision 20 ("swap tags
// only" choice during P-048 close); reactivated at P-048.1 after Banks's
// real-send-to-Gmail smoke surfaced CC validator flagging "Your tracking
// pixel is missing" on pushed campaigns. Token spelling + placement
// confirmed against CC v3 Custom Code design guide (v3.developer.
// constantcontact.com/api_guide/design_code_emails.html): "Add the tag
// `[[trackingImage]]` inside your email's HTML `<body>` element to allow
// Constant Contact to track when contacts open your email." Placed
// immediately before `</body>` (lowest-blast-radius position; standard
// pattern across email-builder tooling) — outside the centering wrapper
// table so CC's pixel substitution doesn't end up inside a table cell
// where Outlook desktop's Word renderer might quirk it.
//
// The placeholder ships in EVERY rendered email — V1 copy/paste output
// too. CC strips the pixel from inbound-paste content if it's not
// already there OR processes it identically to the API ingest path; no
// V1-vs-V2-only branching needed. The bracket-form token passes through
// the html kernel's escape because `[` / `]` aren't in the escape set
// (D-021 + D-024), so the literal `[[trackingImage]]` reaches the
// rendered HTML byte-exact.

import { html } from './html'
import { tokens } from '../tokens'
import { type HtmlString } from '../types'

interface DocumentShellArgs {
  title: string
  body: HtmlString
}

export function documentShell({ title, body }: DocumentShellArgs): HtmlString {
  // **P-037.13: belt-and-suspenders font-family enforcement.** Banks's audit
  // surfaced serif fallbacks in places where a `<td>` didn't carry inline
  // font-family and its child element (often a sanitized richText `<p>`
  // nested inside the `text` primitive's `<p>`, or a link primitive output
  // before P-037.13's primitive-side fix) inherited the user-agent default.
  // Layered defense:
  //
  // 1. `body { font-family: ... }` inline style cascades through every
  //    text-bearing descendant in clients that respect body styles (Apple
  //    Mail, Gmail, modern Outlook.com).
  // 2. `<style>` selector list targets the common text-bearing elements
  //    explicitly — defense for clients that ignore body inheritance but
  //    do respect `<style>` (most webmail + modern desktop).
  // 3. Outlook desktop ignores both layers above; it relies on the inline
  //    `font-family` set by every text-emitting primitive (`heading`,
  //    `text`, `button`, `link` after P-037.13). The two `<style>`-layer
  //    defenses cover the long tail of edge cases where a wrapping cell
  //    might have been the only source of font-family (none today, but
  //    insurance against future regressions).
  const containerWidth = tokens.layout.containerWidth
  // prettier-ignore
  return html`<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${title}</title>
<style>
body, table, td, p, h1, h2, h3, h4, h5, h6, span, a, div { font-family: ${tokens.type.fontFamily}; }
@media (max-width: ${tokens.layout.mobileBreakpoint}px) {
  td[width] { width: 100% !important; display: block !important; }
  .s7gc { padding-top: 20px !important; }
}
</style>
<!--[if mso]><style>body{background-color:#ffffff!important;}</style><![endif]-->
</head>
<body style="margin:0;padding:0;background-color:${tokens.color['bg-page']};font-family:${tokens.type.fontFamily};">
<table role="presentation" align="center" width="${containerWidth}" bgcolor="#ffffff" cellpadding="0" cellspacing="0" border="0" style="margin: 0 auto; max-width: ${containerWidth}px; background-color: #ffffff;"><tr><td bgcolor="#ffffff" style="padding: 0; background-color: #ffffff;">
${body}
</td></tr></table>
[[trackingImage]]
</body>
</html>`
}
